import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuestionsFeed, useLeaderboard, useEnrollments, useClassComments, usePostComment, useDeleteComment, useActiveSessions, useCreateSession, useDeleteSession, useSessionHistory, useClassUploads, useClassFriendUploads, useFriends, type StudySession } from '../lib/api';
import type { ClassComment } from '@shared/types';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { deriveCategories, buildCategoryIndex } from '../lib/topic-tags';
import { getFlaggedQuestions } from '../lib/flagged-questions';
import { getReviewedFlashcardIds, getFlaggedFlashcardIds } from '../lib/flashcard-progress';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Stagger, PageTransition, Modal, CountUp, Skeleton } from '../lib/motion';
import { ClassInvitePanel } from '../components/ClassInvitePanel';

// Question source scope for the session pickers.
type QSource = 'all' | 'mine' | 'friends' | 'professor';

export function ClassPage() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { data: feed, isLoading: feedLoading } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: lb } = useLeaderboard('class_section', sectionId ?? null);
  const { data: enrollData } = useEnrollments();
  const { data: commentsData } = useClassComments(sectionId);
  const { data: sessionsData } = useActiveSessions({ classSectionId: sectionId ?? null });
  const createSession = useCreateSession();
  const deleteSession = useDeleteSession();
  const postComment = usePostComment();
  const deleteComment = useDeleteComment();
  const { data: historyData } = useSessionHistory({ classSectionId: sectionId ?? null });
  const sessionHistory = historyData?.sessions ?? [];

  const enrollment = enrollData?.enrollments?.find((e: any) => e.id === sectionId);
  const questionCount = feed?.questions?.length ?? 0;
  const hasQuestions = questionCount > 0;
  const activeSessions = sessionsData?.sessions ?? [];

  const [showFullLb, setShowFullLb] = useState(false);
  const [showModePicker, setShowModePicker] = useState(false);
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [showFriendsUpsell, setShowFriendsUpsell] = useState(false);
  const [sessionCount, setSessionCount] = useState(20);
  const [sessionName, setSessionName] = useState('');
  const [studyMode, setStudyMode] = useState<'questions' | 'flashcards' | 'quick'>('questions');
  const [questionFilter, setQuestionFilter] = useState<QSource>('all');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Uploads usable as session filters (under My Questions / Friends Q)
  const { data: uploadsData } = useClassUploads({ class_section_id: sectionId });
  const myUploads = uploadsData?.uploads ?? [];
  const { data: friendUploadsData } = useClassFriendUploads({ class_section_id: sectionId });
  const friendUploads = friendUploadsData?.uploads ?? [];
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter(f => f.status === 'accepted').map(f => f.id)),
    [friendsData],
  );
  const [selectedUploadIds, setSelectedUploadIds] = useState<Set<string>>(new Set());
  const toggleUpload = (id: string) => {
    setSelectedUploadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter a pool by question source scope (mine / friends) and selected uploads.
  const applyScope = (pool: any[], scope: QSource): any[] => {
    let p = pool;
    if (scope === 'mine' && feed?.currentUserId) {
      p = p.filter((q: any) => q.authorUserId === feed.currentUserId);
    } else if (scope === 'friends') {
      p = p.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    } else if (scope === 'professor') {
      // authoredByProfessor is derived server side from the author being a
      // currently-verified professor, so it self-corrects on unverify.
      p = p.filter((q: any) => q.authoredByProfessor);
    }
    if (scope !== 'all' && selectedUploadIds.size > 0) {
      p = p.filter((q: any) => q.ingestionJobId && selectedUploadIds.has(q.ingestionJobId));
    }
    return p;
  };

  // Entitlements from feed
  const canUseFlashcards = feed?.entitlements?.canUseFlashcards ?? false;
  const canUseFriendDecks = feed?.entitlements?.canUseFriendDecks ?? false;

  // All flashcard questions
  const allFlashcards = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'flashcard'),
    [feed?.questions],
  );
  const hasFlashcards = allFlashcards.length > 0;

  // All quick memory questions
  const allQuickQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'quick_mcq'),
    [feed?.questions],
  );
  const hasQuick = allQuickQuestions.length > 0;

  // Flashcard topic indexing (uses payload.topic as the category key)
  const fcCategoryIndex = useMemo(() => {
    const index = new Map<string, Set<string>>();
    for (const q of allFlashcards) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = index.get(topic);
      if (!set) { set = new Set(); index.set(topic, set); }
      set.add(q.id);
    }
    return index;
  }, [allFlashcards]);

  const fcAvailableTopics = useMemo(() => {
    return Array.from(fcCategoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [fcCategoryIndex]);

  // Flashcard filter states
  const [fcFilter, setFcFilter] = useState<QSource>('all');
  const [fcSelectedTopics, setFcSelectedTopics] = useState<Set<string>>(new Set());
  const [fcUnansweredOnly, setFcUnansweredOnly] = useState(false);
  const [fcFlaggedOnly, setFcFlaggedOnly] = useState(false);
  const fcAllSelected = fcSelectedTopics.size === 0;

  const fcReviewedSet = useMemo(() => getReviewedFlashcardIds(), [allFlashcards]);
  const fcFlaggedSet = useMemo(() => getFlaggedFlashcardIds(), [allFlashcards]);

  const fcFilteredPool = useMemo(() => {
    let pool = applyScope(allFlashcards, fcFilter);
    if (!fcAllSelected) {
      const ids = new Set<string>();
      for (const cat of fcSelectedTopics) {
        const qIds = fcCategoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    if (fcUnansweredOnly) pool = pool.filter(q => !fcReviewedSet.has(q.id));
    if (fcFlaggedOnly) pool = pool.filter(q => fcFlaggedSet.has(q.id));
    return pool;
  }, [allFlashcards, fcFilter, feed?.currentUserId, friendIds, fcAllSelected, fcSelectedTopics, fcCategoryIndex, fcUnansweredOnly, fcFlaggedOnly, fcReviewedSet, fcFlaggedSet, selectedUploadIds]);

  // Per-topic counts for flashcards (reactive to filters)
  const fcFilteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of fcCategoryIndex.entries()) {
      let topicCards = applyScope(allFlashcards.filter(q => qIds.has(q.id)), fcFilter);
      if (fcUnansweredOnly) topicCards = topicCards.filter(q => !fcReviewedSet.has(q.id));
      if (fcFlaggedOnly) topicCards = topicCards.filter(q => fcFlaggedSet.has(q.id));
      counts.set(name, topicCards.length);
    }
    return counts;
  }, [fcCategoryIndex, allFlashcards, fcFilter, feed?.currentUserId, friendIds, fcUnansweredOnly, fcFlaggedOnly, fcReviewedSet, fcFlaggedSet, selectedUploadIds]);

  // Extract unique topics from questions (exclude flashcards + quick memory)
  const allStudyableQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq'),
    [feed?.questions],
  );
  const studyableQuestions = useMemo(
    () => applyScope(allStudyableQuestions, questionFilter),
    [allStudyableQuestions, questionFilter, feed?.currentUserId, friendIds, selectedUploadIds],
  );

  const categoryIndex = useMemo(
    () => buildCategoryIndex(studyableQuestions),
    [studyableQuestions],
  );
  const availableTopics = useMemo(() => {
    return Array.from(categoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [categoryIndex]);

  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [answeredWrongOnly, setAnsweredWrongOnly] = useState(false);
  const allSelected = selectedTopics.size === 0; // empty = all

  const answeredSet = useMemo(
    () => new Set(feed?.answeredQuestionIds ?? []),
    [feed?.answeredQuestionIds],
  );
  const wrongSet = useMemo(
    () => new Set(feed?.wrongQuestionIds ?? []),
    [feed?.wrongQuestionIds],
  );

  // ── Quick Memory filter states ──────────────────────────────────────────
  const [qmFilter, setQmFilter] = useState<QSource>('all');
  const [qmSelectedTopics, setQmSelectedTopics] = useState<Set<string>>(new Set());
  const [qmUnansweredOnly, setQmUnansweredOnly] = useState(false);
  const [qmAnsweredWrongOnly, setQmAnsweredWrongOnly] = useState(false);
  const qmAllSelected = qmSelectedTopics.size === 0;

  // Quick topic indexing (uses payload.topic as the category key)
  const qmCategoryIndex = useMemo(() => {
    const index = new Map<string, Set<string>>();
    for (const q of allQuickQuestions) {
      const topic = (q.payload as any)?.topic || 'General';
      let set = index.get(topic);
      if (!set) { set = new Set(); index.set(topic, set); }
      set.add(q.id);
    }
    return index;
  }, [allQuickQuestions]);

  const qmAvailableTopics = useMemo(() => {
    return Array.from(qmCategoryIndex.entries())
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [qmCategoryIndex]);

  // Quick pool BEFORE topic selection (mine/unanswered/wrong applied) — drives
  // per-topic counts; topic selection applied on top for the session pool.
  const qmBasePool = useMemo(() => {
    let pool = applyScope(allQuickQuestions, qmFilter);
    if (qmUnansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (qmAnsweredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  }, [allQuickQuestions, qmFilter, feed?.currentUserId, friendIds, selectedUploadIds, qmUnansweredOnly, qmAnsweredWrongOnly, answeredSet, wrongSet]);

  const qmFilteredPool = useMemo(() => {
    if (qmAllSelected) return qmBasePool;
    const ids = new Set<string>();
    for (const cat of qmSelectedTopics) {
      const qIds = qmCategoryIndex.get(cat);
      if (qIds) qIds.forEach(id => ids.add(id));
    }
    return qmBasePool.filter(q => ids.has(q.id));
  }, [qmBasePool, qmAllSelected, qmSelectedTopics, qmCategoryIndex]);

  const qmFilteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const poolIds = new Set(qmBasePool.map(q => q.id));
    for (const [name, qIds] of qmCategoryIndex.entries()) {
      let n = 0;
      qIds.forEach(id => { if (poolIds.has(id)) n++; });
      counts.set(name, n);
    }
    return counts;
  }, [qmCategoryIndex, qmBasePool]);

  const handleStartStudy = () => {
    // Wait for the feed before deciding the picker path. Before it loads,
    // hasFlashcards/hasQuick/availableTopics are empty, which would skip the
    // mode/topic picker and drop the user straight into studying. (Bug: first
    // tap on a freshly-opened section bypassed the session-setup flow.)
    if (feedLoading) return;
    if (hasFlashcards || hasQuick) {
      setShowModePicker(true);
    } else {
      openQuestionTopicPicker();
    }
  };

  const openQuestionTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('questions');
    setSelectedTopics(new Set());
    setQuestionFilter('all');
    setUnansweredOnly(false);
    setAnsweredWrongOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    // Always go through the picker, even for a single-topic class: the picker is
    // what creates a study SESSION. The old single-topic shortcut navigated
    // straight to /study without a session, so position was never persisted and
    // any reload restarted the user at question 1.
    setShowTopicPicker(true);
  };

  const openFlashcardTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('flashcards');
    setFcSelectedTopics(new Set());
    setFcFilter('all');
    setFcUnansweredOnly(false);
    setFcFlaggedOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const openQuickTopicPicker = () => {
    setShowModePicker(false);
    setStudyMode('quick');
    setQmSelectedTopics(new Set());
    setQmFilter('all');
    setQmUnansweredOnly(false);
    setQmAnsweredWrongOnly(false);
    setSelectedUploadIds(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  };

  // Apply active filters to a question pool
  const applyFilters = (pool: typeof studyableQuestions) => {
    if (unansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (answeredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  };

  // Compute how many unique questions are matched by current selection
  const selectedQuestionCount = useMemo(() => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach((id) => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.length;
  }, [allSelected, selectedTopics, categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  // Per-topic filtered counts (reactive to unanswered/wrong toggles)
  const filteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of categoryIndex.entries()) {
      const topicQuestions = studyableQuestions.filter(q => qIds.has(q.id));
      counts.set(name, applyFilters(topicQuestions).length);
    }
    return counts;
  }, [categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const getFilteredQuestionIds = (): string[] => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.map(q => q.id);
  };

  // Optional user-given session name + de-duplication so open sessions are
  // distinguishable ("Cardiovascular", "Cardiovascular (2)", ...).
  const uniqueSessionLabel = (base: string): string => {
    const name = base.trim() || 'Study Session';
    const taken = new Set(activeSessions.map(s => s.label).filter(Boolean));
    if (!taken.has(name)) return name;
    let n = 2;
    while (taken.has(`${name} (${n})`)) n++;
    return `${name} (${n})`;
  };

  // Live default labels, used as both the name-input placeholder and the
  // fallback when the user leaves the name blank.
  const scopeLabel = (scope: QSource, kind: string): string =>
    scope === 'professor' ? `Professor ${kind}`
    : scope === 'friends' ? `Friends' ${kind}`
    : scope === 'mine' ? `My ${kind}`
    : `All ${kind}`;

  const questionsDefaultLabel = useMemo(() => {
    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const prefix = questionFilter === 'friends' ? "Friends: " : '';
    return allSelected
      ? scopeLabel(questionFilter, 'Topics')
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [allSelected, selectedTopics, questionFilter, selectedUploadIds]);

  const quickDefaultLabel = useMemo(() => {
    const topicNames = qmAllSelected ? [] : Array.from(qmSelectedTopics);
    const prefix = qmFilter === 'friends' ? "Friends: " : '';
    return qmAllSelected
      ? (qmFilter === 'all' ? 'Quick Study' : scopeLabel(qmFilter, 'Quick'))
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [qmAllSelected, qmSelectedTopics, qmFilter, selectedUploadIds]);

  const flashDefaultLabel = useMemo(() => {
    const topicNames = fcAllSelected ? [] : Array.from(fcSelectedTopics);
    const prefix = fcFilter === 'friends' ? "Friends: " : '';
    return fcAllSelected
      ? (fcFilter === 'all' ? 'All Flash Cards' : scopeLabel(fcFilter, 'Cards'))
      : prefix + topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [fcAllSelected, fcSelectedTopics, fcFilter, selectedUploadIds]);

  const confirmTopics = async () => {
    setShowTopicPicker(false);
    const allIds = getFilteredQuestionIds();
    if (allIds.length === 0) return;

    // Shuffle, then take the user's chosen count (capped at what's available)
    // so each session is a fresh sample of the selected topics.
    for (let i = allIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allIds[i], allIds[j]] = [allIds[j]!, allIds[i]!];
    }
    const count = Math.min(Math.max(1, sessionCount), allIds.length);
    const questionIds = allIds.slice(0, count);

    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const uploadIds = questionFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || questionsDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: allSelected && !uploadIds ? null : { topics: topicNames, ...(uploadIds ? { uploadIds } : {}) },
      questionIds,
      label,
    });

    navigate(`/study/class/${sectionId}?session=${result.session.id}`);
  };

  const confirmQuickTopics = async () => {
    setShowTopicPicker(false);
    const allIds = qmFilteredPool.map(q => q.id);
    if (allIds.length === 0) return;

    // Shuffle, then take the user's chosen count (capped at what's available).
    for (let i = allIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allIds[i], allIds[j]] = [allIds[j]!, allIds[i]!];
    }
    const count = Math.min(Math.max(1, sessionCount), allIds.length);
    const questionIds = allIds.slice(0, count);

    const topicNames = qmAllSelected ? [] : Array.from(qmSelectedTopics);
    const uploadIds = qmFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || quickDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: topicNames, mode: 'quick', ...(uploadIds ? { uploadIds } : {}) },
      questionIds,
      label,
    });

    navigate(`/quickmem/${sectionId}?session=${result.session.id}`);
  };

  const confirmFlashcardTopics = async () => {
    setShowTopicPicker(false);
    const questionIds = fcFilteredPool.map(q => q.id);
    if (questionIds.length === 0) return;

    // Shuffle for variety
    for (let i = questionIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = questionIds[i]!;
      questionIds[i] = questionIds[j]!;
      questionIds[j] = tmp;
    }

    const topicNames = fcAllSelected ? [] : Array.from(fcSelectedTopics);
    const fcUploadIds = fcFilter !== 'all' && selectedUploadIds.size > 0 ? Array.from(selectedUploadIds) : undefined;
    const label = uniqueSessionLabel(sessionName || flashDefaultLabel);

    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: topicNames, mode: 'flashcard', ...(fcUploadIds ? { uploadIds: fcUploadIds } : {}) } as any,
      questionIds,
      label,
    });

    navigate(`/flashcards/${sectionId}?session=${result.session.id}`);
  };

  const resumeSession = (session: StudySession) => {
    const mode = (session.filters as any)?.mode;
    if (mode === 'flashcard') {
      navigate(`/flashcards/${sectionId}?session=${session.id}`);
    } else if (mode === 'quick') {
      navigate(`/quickmem/${sectionId}?session=${session.id}`);
    } else {
      navigate(`/study/class/${sectionId}?session=${session.id}`);
    }
  };

  // Full leaderboard overlay
  if (showFullLb) {
    return (
      <PageTransition className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 py-5">
          <Pressable
            onClick={() => setShowFullLb(false)}
            className="flex items-center gap-1.5 text-squishy-primary font-semibold mb-5 text-sm min-h-[44px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to class
          </Pressable>
          <h1 className="font-display text-2xl font-semibold text-squishy-dark tracking-tight mb-1">Class Leaderboard</h1>
          <p className="text-xs text-slate-500 mb-5 uppercase tracking-wider">Rolling 30 days</p>
          <FadeIn>
            <ol className="bg-white rounded-2xl border border-slate-200/80 shadow-card divide-y divide-slate-100 overflow-hidden">
              {lb?.entries.map((e) => (
                <li key={e.userId} className={`flex items-center gap-3 px-4 py-3 ${e.isCurrentUser ? 'bg-teal-50/70' : ''}`}>
                  <div className={`w-8 text-right font-mono text-sm ${e.rank <= 3 ? 'font-semibold text-squishy-primary' : 'text-slate-400'}`}>{e.rank}</div>
                  <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-xs font-medium ring-2 ring-white shadow-sm">
                    {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
                  </div>
                  <div className="flex-1 font-medium text-sm inline-flex items-center gap-1 min-w-0">
                    <span className="truncate">{e.displayName}</span>
                    {e.verifiedStudent && <VerifiedBadge />}
                  </div>
                  <div className="text-[11px] font-medium text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">L{e.level}</div>
                  <div className="text-sm font-mono text-squishy-dark w-20 text-right tabular-nums">{e.xp.toLocaleString()} XP</div>
                </li>
              ))}
              {(!lb?.entries || lb.entries.length === 0) && (
                <li className="p-8 text-center text-slate-400 text-sm">No activity in the last 30 days</li>
              )}
            </ol>
          </FadeIn>
          {lb?.currentUserRank && (
            <p className="text-xs text-slate-500 mt-4 text-center">Your rank: <span className="font-semibold text-squishy-dark">#{lb.currentUserRank}</span> of {lb.totalUsers}</p>
          )}
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="space-y-6 max-w-lg mx-auto pb-4">
      {sectionId && <ClassInvitePanel sectionId={sectionId} />}
      {/* Hero: class identity + stats + primary CTA */}
      <FadeIn>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-squishy-navy via-squishy-navySoft to-squishy-purple p-5 pt-6 shadow-pop">
          {/* Decorative glow blobs */}
          <div className="pointer-events-none absolute -top-16 -right-12 w-48 h-48 rounded-full bg-squishy-teal/25 blur-3xl" aria-hidden />
          <div className="pointer-events-none absolute -bottom-20 -left-10 w-44 h-44 rounded-full bg-squishy-gold/15 blur-3xl" aria-hidden />
          <div className="relative">
            <h1 className="font-display text-2xl font-semibold text-white tracking-tight truncate">
              {enrollment?.class_code ?? 'Class'}
            </h1>
            {enrollment && (
              <div className="text-xs text-white/60 truncate mt-0.5">
                {enrollment.professor_name ? `Prof. ${enrollment.professor_name}` : enrollment.school_name}
                {enrollment.term ? ` · ${enrollment.term}` : ''}
              </div>
            )}
            {/* Stat chips */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {feedLoading ? (
                <>
                  <Skeleton className="h-7 w-24 rounded-full bg-white/20" />
                  <Skeleton className="h-7 w-24 rounded-full bg-white/20" />
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-teal-200 border border-white/10">
                    <CountUp value={allStudyableQuestions.length} className="tabular-nums text-white" /> questions
                  </span>
                  {hasFlashcards && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-violet-200 border border-white/10">
                      <CountUp value={allFlashcards.length} className="tabular-nums text-white" /> cards
                    </span>
                  )}
                  {hasQuick && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-amber-200 border border-white/10">
                      <CountUp value={allQuickQuestions.length} className="tabular-nums text-white" /> quick
                    </span>
                  )}
                </>
              )}
            </div>
            {/* Primary CTA */}
            {!feedLoading && !hasQuestions ? (
              <div className="mt-5 rounded-2xl border border-dashed border-white/25 bg-white/5 p-5 text-center space-y-3">
                <h2 className="text-sm font-semibold text-white">Nothing to study yet</h2>
                <p className="text-white/60 text-xs">Add notes or slides and we will turn them into practice questions.</p>
                <Link to={`/upload?class=${sectionId}`}
                      className="inline-flex items-center justify-center gap-2 min-h-[44px] bg-squishy-teal text-squishy-navy font-bold px-6 py-2.5 rounded-xl hover:bg-teal-300 transition-colors text-sm shadow-soft">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add material
                </Link>
              </div>
            ) : (
              <div className="mt-5 flex items-stretch gap-2">
                <Pressable
                  haptic="medium"
                  onClick={handleStartStudy}
                  disabled={feedLoading}
                  className="flex-1 min-h-[52px] bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white text-center py-3.5 rounded-2xl font-bold text-[15px] shadow-pop disabled:opacity-60 disabled:shadow-none"
                >
                  {feedLoading ? 'Loading your class...' : 'Start studying'}
                </Pressable>
                <Link to={`/upload?class=${sectionId}`}
                      aria-label="Add material"
                      className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white px-4 rounded-2xl font-semibold hover:bg-white/20 transition-colors text-sm backdrop-blur"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add
                </Link>
              </div>
            )}
          </div>
        </div>
      </FadeIn>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Continue studying</h2>
          <Stagger className="space-y-2">
            {activeSessions.map(session => {
              const mode = (session.filters as any)?.mode;
              return (
                <Stagger.Item key={session.id}>
                  <ClassSessionCard
                    session={session}
                    isFlashcard={mode === 'flashcard'}
                    isQuick={mode === 'quick'}
                    onResume={() => resumeSession(session)}
                    onDelete={() => setConfirmDeleteId(session.id)}
                  />
                </Stagger.Item>
              );
            })}
          </Stagger>
        </section>
      )}

      {/* Marked for Review (Questions) */}
      <ClassMarkedForReviewCard
        allQuestions={allStudyableQuestions}
        createSession={createSession}
        navigate={navigate}
        sectionId={sectionId!}
      />

      {/* Marked for Review (Flashcards) */}
      <FlashcardReviewCard
        allFlashcards={allFlashcards}
        createSession={createSession}
        navigate={navigate}
        sectionId={sectionId!}
      />

      {/* Past Sessions */}
      {sessionHistory.length > 0 && (
        <ClassPastSessionsSection sessions={sessionHistory} />
      )}

      {/* Leaderboard — top 5 */}
      <section className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-card">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="font-semibold text-squishy-dark text-sm">Leaderboard</h2>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">Last 30 days</span>
        </div>
        <ol className="divide-y divide-slate-100">
          {lb?.entries.slice(0, 5).map((e) => (
            <li key={e.userId} className={`flex items-center gap-3 px-5 py-2.5 ${e.isCurrentUser ? 'bg-teal-50/70' : ''}`}>
              <span className={`w-5 text-right text-xs font-mono ${e.rank <= 3 ? 'font-semibold text-squishy-primary' : 'text-slate-400'}`}>{e.rank}</span>
              <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[10px] font-medium shrink-0 ring-2 ring-white shadow-sm">
                {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
              </div>
              <span className="flex-1 text-sm font-medium truncate inline-flex items-center gap-1">
                <span className="truncate">{e.displayName}</span>
                {e.verifiedStudent && <VerifiedBadge />}
              </span>
              <span className="text-xs font-mono text-squishy-dark tabular-nums">{e.xp.toLocaleString()} XP</span>
            </li>
          ))}
          {(!lb?.entries || lb.entries.length === 0) && (
            <li className="px-5 py-7 text-center text-slate-400 text-sm">No activity yet. Your class ranking starts with the first session.</li>
          )}
        </ol>
        {lb && lb.entries.length > 5 && (
          <Pressable
            onClick={() => setShowFullLb(true)}
            className="w-full min-h-[44px] py-3 text-center text-squishy-primary text-sm font-semibold border-t border-slate-100 hover:bg-slate-50 transition-colors"
          >
            View full leaderboard
          </Pressable>
        )}
        {lb && lb.entries.length > 0 && lb.entries.length <= 5 && (
          <Pressable
            onClick={() => setShowFullLb(true)}
            className="w-full min-h-[44px] py-3 text-center text-squishy-primary text-sm font-semibold border-t border-slate-100 hover:bg-slate-50 transition-colors"
          >
            View all
          </Pressable>
        )}
      </section>

      {/* Comments */}
      <CommentsSection
        classSectionId={sectionId!}
        comments={commentsData?.comments ?? []}
        entitlements={commentsData?.entitlements}
        postComment={postComment}
        deleteComment={deleteComment}
      />

      {/* Friends' decks upsell (Plus+) */}
      <Modal open={showFriendsUpsell} onClose={() => setShowFriendsUpsell(false)}>
        <div className="bg-white rounded-3xl w-full p-6 text-center shadow-pop">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-amber-500">
              <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="font-display text-lg font-semibold text-squishy-dark">Study your friends' decks</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Practice the questions, flashcards, and quick sets your friends uploaded. Studying friends' decks is a <span className="font-semibold">Plus &amp; Pro</span> feature.
          </p>
          <Link
            to="/profile"
            onClick={() => setShowFriendsUpsell(false)}
            className="mt-5 block w-full min-h-[48px] rounded-xl bg-squishy-primary py-3.5 text-sm font-semibold text-white shadow-soft hover:bg-squishy-tealDark transition-colors"
          >
            Upgrade to Plus (from $1.99/mo)
          </Link>
          <button onClick={() => setShowFriendsUpsell(false)} className="mt-2 w-full min-h-[44px] py-2.5 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors">
            Maybe later
          </button>
        </div>
      </Modal>

      {/* Mode Picker Modal */}
      <Modal open={showModePicker} onClose={() => setShowModePicker(false)} sheet="bottom">
        <div className="bg-white w-full rounded-t-3xl overflow-hidden shadow-pop">
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-200" aria-hidden />
          <div className="px-6 pt-4 pb-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-squishy-dark tracking-tight">Start a session</h2>
              <button onClick={() => setShowModePicker(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600 p-2.5 -mr-2 rounded-full hover:bg-slate-100 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Choose how you want to study.</p>
          </div>
          <Stagger className="px-6 pb-6 pt-1 space-y-2.5">
            <Stagger.Item>
              <Pressable
                onClick={openQuestionTopicPicker}
                className="w-full flex items-center gap-4 p-4 min-h-[64px] rounded-2xl border border-slate-200 bg-white hover:border-squishy-teal hover:bg-teal-50/50 transition-colors shadow-card"
              >
                <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-squishy-primary">
                    <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 3.75a.75.75 0 000 1.5h9a.75.75 0 000-1.5h-9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-squishy-dark">Practice questions</div>
                  <div className="text-xs text-slate-500 mt-0.5">Exam-style items with full rationales. {allStudyableQuestions.length} available.</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-300">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </Pressable>
            </Stagger.Item>
            <Stagger.Item>
              <Pressable
                haptic={canUseFlashcards ? 'light' : null}
                onClick={canUseFlashcards ? openFlashcardTopicPicker : undefined}
                className={`w-full flex items-center gap-4 p-4 min-h-[64px] rounded-2xl border transition-colors ${canUseFlashcards ? 'border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/50 shadow-card' : 'border-slate-100 bg-slate-50 opacity-75'}`}
              >
                <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-500">
                    <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-squishy-dark">Flash cards</div>
                  {canUseFlashcards
                    ? <div className="text-xs text-slate-500 mt-0.5">Active recall, one card at a time. {allFlashcards.length} available.</div>
                    : <div className="text-xs text-violet-500 font-medium mt-0.5">Upgrade to Plus to study flash cards</div>
                  }
                </div>
                {canUseFlashcards ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-300">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-[10px] font-bold text-violet-500 bg-violet-100 dark:bg-violet-500/20 px-2 py-0.5 rounded-md">PLUS</span>
                )}
              </Pressable>
            </Stagger.Item>
            <Stagger.Item>
              <Pressable
                haptic={hasQuick ? 'light' : null}
                onClick={hasQuick ? openQuickTopicPicker : undefined}
                className={`w-full flex items-center gap-4 p-4 min-h-[64px] rounded-2xl border transition-colors ${hasQuick ? 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/50 shadow-card' : 'border-slate-100 bg-slate-50 opacity-75'}`}
              >
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-amber-500">
                    <path fillRule="evenodd" d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[15px] font-semibold text-squishy-dark">Quick study</div>
                  {hasQuick
                    ? <div className="text-xs text-slate-500 mt-0.5">Rapid-fire recall for short breaks. {allQuickQuestions.length} available.</div>
                    : <div className="text-xs text-slate-400 mt-0.5">Add material to generate quick questions</div>
                  }
                </div>
                {hasQuick && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-300">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                )}
              </Pressable>
            </Stagger.Item>
          </Stagger>
        </div>
      </Modal>

      {/* Topic Picker Modal (Questions or Flash Cards) */}
      <Modal open={showTopicPicker && studyMode === 'questions'} onClose={() => setShowTopicPicker(false)} sheet="bottom">
        <div className="bg-white w-full rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-pop">
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-200 shrink-0" aria-hidden />
            <div className="px-6 pt-4 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-squishy-dark tracking-tight">Choose topics</h2>
                <button onClick={() => setShowTopicPicker(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600 p-2.5 -mr-2 rounded-full hover:bg-slate-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Select which topics to study, or keep All selected.</p>
              {/* Source: All / My Questions / Friends Q */}
              <SourceTabs
                value={questionFilter}
                onChange={(s) => { setQuestionFilter(s); setSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allStudyableQuestions.length,
                  mine: allStudyableQuestions.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allStudyableQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                  professor: allStudyableQuestions.filter((q: any) => q.authoredByProfessor).length,
                }}
                accent="text-squishy-primary"
                mineLabel="My Q"
                friendsLabel="Friends Q"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {questionFilter !== 'all' && (
                <UploadFilterChips
                  uploads={questionFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.questionCount}
                  showAuthor={questionFilter === 'friends'}
                  accentClass="border-squishy-primary bg-teal-50 text-squishy-primary"
                />
              )}
              {/* Unanswered Only toggle */}
              <div className="mt-3 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({studyableQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                  </span>
                </div>
                <Switch
                  on={unansweredOnly}
                  onToggle={() => { setUnansweredOnly(v => !v); setAnsweredWrongOnly(false); }}
                  activeClass="bg-squishy-primary"
                />
              </div>
              {/* Answered Wrong Only toggle */}
              <div className="mt-2 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({wrongSet.size} to retry)
                  </span>
                </div>
                <Switch
                  on={answeredWrongOnly}
                  onToggle={() => { setAnsweredWrongOnly(v => !v); setUnansweredOnly(false); }}
                  activeClass="bg-red-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  allSelected
                    ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{applyFilters(studyableQuestions).length} questions</span>
              </button>

              {/* Individual topics */}
              {availableTopics.map((t) => {
                const isSelected = selectedTopics.has(t.name);
                const filteredCount = filteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => toggleTopic(t.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={questionsDefaultLabel || 'e.g. Cardio review'} accent="focus:ring-squishy-primary/30" />
              {/* How many questions this session */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={selectedQuestionCount}
                    value={Math.min(sessionCount, selectedQuestionCount) || ''}
                    onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-center text-sm text-slate-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-squishy-primary/30"
                  />
                  <span className="text-xs text-slate-400">/ {selectedQuestionCount} max</span>
                  <button
                    onClick={() => setSessionCount(selectedQuestionCount)}
                    className="text-xs font-semibold text-squishy-primary hover:underline px-1 py-2"
                  >
                    Max
                  </button>
                </div>
              </div>
              <Pressable
                haptic="medium"
                onClick={confirmTopics}
                disabled={selectedQuestionCount === 0}
                className="w-full min-h-[52px] bg-squishy-primary text-white py-3.5 rounded-2xl font-semibold text-[15px] shadow-soft hover:bg-squishy-tealDark transition-colors disabled:opacity-40 disabled:shadow-none"
              >
                Start studying: {Math.min(Math.max(1, sessionCount), selectedQuestionCount)} question{Math.min(Math.max(1, sessionCount), selectedQuestionCount) !== 1 ? 's' : ''}
              </Pressable>
            </div>
        </div>
      </Modal>

      {/* Flash Card Topic Picker Modal */}
      <Modal open={showTopicPicker && studyMode === 'flashcards'} onClose={() => setShowTopicPicker(false)} sheet="bottom">
        <div className="bg-white w-full rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-pop">
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-200 shrink-0" aria-hidden />
            <div className="px-6 pt-4 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-squishy-dark tracking-tight">Flash card topics</h2>
                <button onClick={() => setShowTopicPicker(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600 p-2.5 -mr-2 rounded-full hover:bg-slate-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Select topics or keep All selected.</p>
              {/* Source: All / My Cards / Friends */}
              <SourceTabs
                value={fcFilter}
                onChange={(s) => { setFcFilter(s); setFcSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allFlashcards.length,
                  mine: allFlashcards.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allFlashcards.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                }}
                accent="text-violet-600"
                mineLabel="My"
                friendsLabel="Friends"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {fcFilter !== 'all' && (
                <UploadFilterChips
                  uploads={fcFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.flashcardCount}
                  showAuthor={fcFilter === 'friends'}
                  accentClass="border-violet-400 bg-violet-50 text-violet-600"
                />
              )}
              {/* Unseen Only toggle */}
              <div className="mt-3 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unseen only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allFlashcards.filter(q => !fcReviewedSet.has(q.id)).length} new)
                  </span>
                </div>
                <Switch
                  on={fcUnansweredOnly}
                  onToggle={() => { setFcUnansweredOnly(v => !v); setFcFlaggedOnly(false); }}
                  activeClass="bg-violet-500"
                />
              </div>
              {/* Marked for Review Only toggle */}
              <div className="mt-2 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Marked for review</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allFlashcards.filter(q => fcFlaggedSet.has(q.id)).length} flagged)
                  </span>
                </div>
                <Switch
                  on={fcFlaggedOnly}
                  onToggle={() => { setFcFlaggedOnly(v => !v); setFcUnansweredOnly(false); }}
                  activeClass="bg-amber-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setFcSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  fcAllSelected
                    ? 'border-violet-400 bg-violet-50 text-violet-600'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{fcFilteredPool.length} cards</span>
              </button>

              {/* Individual topics */}
              {fcAvailableTopics.map((t) => {
                const isSelected = fcSelectedTopics.has(t.name);
                const filteredCount = fcFilteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => {
                      setFcSelectedTopics(prev => {
                        const next = new Set(prev);
                        if (next.has(t.name)) next.delete(t.name);
                        else next.add(t.name);
                        return next;
                      });
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-violet-400 bg-violet-50 text-violet-600'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={flashDefaultLabel || 'e.g. Drug cards'} accent="focus:ring-violet-400/30" />
              <Pressable
                haptic="medium"
                onClick={confirmFlashcardTopics}
                disabled={fcFilteredPool.length === 0}
                className="w-full min-h-[52px] bg-violet-500 text-white py-3.5 rounded-2xl font-semibold text-[15px] shadow-card hover:bg-violet-600 transition-colors disabled:opacity-40 disabled:shadow-none"
              >
                Start flash cards{!fcAllSelected ? `: ${fcFilteredPool.length} card${fcFilteredPool.length !== 1 ? 's' : ''}` : ` (${fcFilteredPool.length})`}
              </Pressable>
            </div>
        </div>
      </Modal>

      {/* Quick Memory Topic Picker Modal */}
      <Modal open={showTopicPicker && studyMode === 'quick'} onClose={() => setShowTopicPicker(false)} sheet="bottom">
        <div className="bg-white w-full rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden shadow-pop">
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-200 shrink-0" aria-hidden />
            <div className="px-6 pt-4 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-squishy-dark tracking-tight">Quick study</h2>
                <button onClick={() => setShowTopicPicker(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600 p-2.5 -mr-2 rounded-full hover:bg-slate-100 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Rapid-fire recall. Tap an answer, see right or wrong, move on.</p>
              {/* Source: All / My Questions / Friends Q */}
              <SourceTabs
                value={qmFilter}
                onChange={(s) => { setQmFilter(s); setQmSelectedTopics(new Set()); setSelectedUploadIds(new Set()); }}
                counts={{
                  all: allQuickQuestions.length,
                  mine: allQuickQuestions.filter((q: any) => q.authorUserId === feed?.currentUserId).length,
                  friends: allQuickQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length,
                }}
                accent="text-amber-600"
                mineLabel="My Q"
                friendsLabel="Friends Q"
                friendsLocked={!canUseFriendDecks}
                onLockedFriends={() => setShowFriendsUpsell(true)}
              />
              {qmFilter !== 'all' && (
                <UploadFilterChips
                  uploads={qmFilter === 'friends' ? friendUploads : myUploads}
                  selected={selectedUploadIds}
                  onToggle={toggleUpload}
                  countFor={(u) => u.quickCount}
                  showAuthor={qmFilter === 'friends'}
                  accentClass="border-amber-400 bg-amber-50 text-amber-600"
                />
              )}
              {/* Unanswered Only toggle */}
              <div className="mt-3 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allQuickQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                  </span>
                </div>
                <Switch
                  on={qmUnansweredOnly}
                  onToggle={() => { setQmUnansweredOnly(v => !v); setQmAnsweredWrongOnly(false); }}
                  activeClass="bg-amber-500"
                />
              </div>
              {/* Answered Wrong Only toggle */}
              <div className="mt-2 flex items-center justify-between min-h-[32px]">
                <div>
                  <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                  <span className="ml-2 text-xs text-slate-400">
                    ({allQuickQuestions.filter(q => wrongSet.has(q.id)).length} to retry)
                  </span>
                </div>
                <Switch
                  on={qmAnsweredWrongOnly}
                  onToggle={() => { setQmAnsweredWrongOnly(v => !v); setQmUnansweredOnly(false); }}
                  activeClass="bg-red-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {/* All option */}
              <button
                onClick={() => setQmSelectedTopics(new Set())}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                  qmAllSelected
                    ? 'border-amber-400 bg-amber-50 text-amber-600'
                    : 'border-slate-200 text-slate-600'
                }`}
              >
                <span className="font-medium">All Topics</span>
                <span className="text-xs text-slate-400">{qmBasePool.length} questions</span>
              </button>

              {/* Individual topics */}
              {qmAvailableTopics.map((t) => {
                const isSelected = qmSelectedTopics.has(t.name);
                const filteredCount = qmFilteredTopicCounts.get(t.name) ?? t.count;
                return (
                  <button
                    key={t.name}
                    onClick={() => {
                      setQmSelectedTopics(prev => {
                        const next = new Set(prev);
                        if (next.has(t.name)) next.delete(t.name);
                        else next.add(t.name);
                        return next;
                      });
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'border-amber-400 bg-amber-50 text-amber-600'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium text-left">{t.name}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                  </button>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 space-y-3">
              <SessionNameField value={sessionName} onChange={setSessionName} placeholder={quickDefaultLabel || 'e.g. Lab values'} accent="focus:ring-amber-400/30" />
              {/* How many questions this session */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={qmFilteredPool.length}
                    value={Math.min(sessionCount, qmFilteredPool.length) || ''}
                    onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-center text-sm text-slate-700 tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                  />
                  <span className="text-xs text-slate-400">/ {qmFilteredPool.length} max</span>
                  <button
                    onClick={() => setSessionCount(qmFilteredPool.length)}
                    className="text-xs font-semibold text-amber-600 hover:underline px-1 py-2"
                  >
                    Max
                  </button>
                </div>
              </div>
              <Pressable
                haptic="medium"
                onClick={confirmQuickTopics}
                disabled={qmFilteredPool.length === 0}
                className="w-full min-h-[52px] bg-amber-500 text-white py-3.5 rounded-2xl font-semibold text-[15px] shadow-card hover:bg-amber-600 transition-colors disabled:opacity-40 disabled:shadow-none"
              >
                Start quick study: {Math.min(Math.max(1, sessionCount), qmFilteredPool.length)} question{Math.min(Math.max(1, sessionCount), qmFilteredPool.length) !== 1 ? 's' : ''}
              </Pressable>
            </div>
        </div>
      </Modal>

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <div className="bg-white rounded-3xl p-6 w-full shadow-pop text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-red-400">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500 mt-1">Your progress will be lost and can't be recovered.</p>
          </div>
          <div className="flex gap-2">
            <Pressable
              onClick={() => setConfirmDeleteId(null)}
              className="flex-1 min-h-[44px] py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Keep
            </Pressable>
            <Pressable
              haptic="medium"
              onClick={() => { if (confirmDeleteId) { deleteSession.mutate(confirmDeleteId); } setConfirmDeleteId(null); }}
              className="flex-1 min-h-[44px] py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
            >
              Discard
            </Pressable>
          </div>
        </div>
      </Modal>
    </PageTransition>
  );
}

// ── Marked for Review ─────────────────────────────────────────────────────────

function ClassMarkedForReviewCard({ allQuestions, createSession, navigate, sectionId }: {
  allQuestions: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sectionId: string;
}) {
  const flagged = getFlaggedQuestions();
  const questionIds = allQuestions.map(q => q.id);
  const flaggedInContext = flagged.filter(id => questionIds.includes(id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      questionIds: flaggedInContext,
      label: 'Marked for Review',
    });
    navigate(`/study/class/${sectionId}?session=${result.session.id}`);
  };

  return (
    <Pressable
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-2xl p-4 min-h-[64px] flex items-center gap-3 shadow-card hover:border-amber-300 transition-colors disabled:opacity-50"
    >
      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-amber-400">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Marked for review</div>
        <div className="text-xs text-slate-500">{flaggedInContext.length} question{flaggedInContext.length !== 1 ? 's' : ''} you flagged, ready for a second pass</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-amber-300">
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </Pressable>
  );
}

// ── Past Sessions ─────────────────────────────────────────────────────────────

function ClassPastSessionsSection({ sessions }: { sessions: StudySession[] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-card">
      <Pressable
        haptic={null}
        onClick={() => setExpanded(!expanded)}
        className="w-full min-h-[48px] flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
      >
        <h2 className="font-semibold text-squishy-dark text-sm">Past sessions</h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{sessions.length} completed</span>
          <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={springs.snappy} className="inline-flex">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </motion.span>
        </div>
      </Pressable>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {sessions.map(s => {
                const accuracy = s.questionsAnswered > 0 ? Math.round((s.correctCount / s.questionsAnswered) * 100) : 0;
                const date = s.endedAt ? new Date(s.endedAt).toLocaleDateString() : new Date(s.startedAt).toLocaleDateString();

                return (
                  <div key={s.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-squishy-dark truncate">{s.label || 'All Topics'}</div>
                      <div className="flex items-center gap-2.5 text-[10px] text-slate-400 mt-0.5">
                        <span>{date}</span>
                        <span>{'\u00B7'}</span>
                        <span>{s.questionsAnswered} answered</span>
                        <span>{'\u00B7'}</span>
                        <span className={`font-semibold ${accuracy >= 70 ? 'text-green-600' : accuracy >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{accuracy}%</span>
                        <span>{'\u00B7'}</span>
                        <span className="text-emerald-600 font-semibold">+{s.xpEarned} XP</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Session Card ──────────────────────────────────────────────────────────────

function ClassSessionCard({ session, isFlashcard, isQuick, onResume, onDelete }: {
  session: StudySession;
  isFlashcard?: boolean;
  isQuick?: boolean;
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;
  const barColor = isFlashcard ? 'bg-violet-400' : isQuick ? 'bg-amber-400' : 'bg-teal-400';
  const btnColor = isFlashcard ? 'bg-violet-500' : isQuick ? 'bg-amber-500' : 'bg-squishy-primary';

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-card active:bg-slate-50 transition-colors">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onResume}>
        <div className="flex items-center gap-2">
          {isFlashcard && (
            <span className="text-[9px] font-bold text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded-md shrink-0">CARDS</span>
          )}
          {isQuick && (
            <span className="text-[9px] font-bold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-md shrink-0">QUICK</span>
          )}
          <span className="text-[13px] font-semibold text-squishy-dark truncate">{session.label || 'All Topics'}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{remaining} left</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className={`h-full ${barColor} rounded-full`}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ ...springs.soft, delay: 0.15 }}
            />
          </div>
          <span className="text-[10px] font-medium text-slate-400 shrink-0 w-7 text-right tabular-nums">{progress}%</span>
        </div>
      </div>
      <Pressable onClick={onResume} className={`${btnColor} text-white px-3.5 py-2 min-h-[36px] rounded-xl text-xs font-bold shrink-0 shadow-sm`}>
        Resume
      </Pressable>
      <Pressable
        haptic={null}
        aria-label="Discard session"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="text-slate-300 hover:text-red-400 text-sm shrink-0 transition-colors p-2 -mr-1"
      >
        &#x2715;
      </Pressable>
    </div>
  );
}

// ── Flashcard Review Card ─────────────────────────────────────────────────────

function FlashcardReviewCard({ allFlashcards, createSession, navigate, sectionId }: {
  allFlashcards: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sectionId: string;
}) {
  const flaggedSet = getFlaggedFlashcardIds();
  const flaggedInContext = allFlashcards.filter(q => flaggedSet.has(q.id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const questionIds = flaggedInContext.map(q => q.id);
    // Shuffle
    for (let i = questionIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = questionIds[i]!;
      questionIds[i] = questionIds[j]!;
      questionIds[j] = tmp;
    }
    const result = await createSession.mutateAsync({
      classSectionId: sectionId,
      filters: { topics: [], mode: 'flashcard' } as any,
      questionIds,
      label: 'Review Flagged Cards',
    });
    navigate(`/flashcards/${sectionId}?session=${result.session.id}`);
  };

  return (
    <Pressable
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-gradient-to-r from-violet-50 to-white border border-violet-200 rounded-2xl p-4 min-h-[64px] flex items-center gap-3 shadow-card hover:border-violet-300 transition-colors disabled:opacity-50"
    >
      <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-400">
          <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Flagged flash cards</div>
        <div className="text-xs text-slate-500">{flaggedInContext.length} card{flaggedInContext.length !== 1 ? 's' : ''} to review</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-violet-300">
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </Pressable>
  );
}

// ── Switch ────────────────────────────────────────────────────────────────────
// Presentational toggle. The caller owns all state changes via onToggle.

function Switch({ on, onToggle, activeClass }: { on: boolean; onToggle: () => void; activeClass: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className={`relative rounded-full transition-colors duration-200 shrink-0 ${on ? activeClass : 'bg-slate-300'}`}
      style={{ width: 46, height: 26 }}
    >
      <motion.span
        className="absolute top-[3px] left-[3px] block bg-white rounded-full shadow"
        style={{ width: 20, height: 20 }}
        animate={{ x: on ? 20 : 0 }}
        transition={springs.snappy}
      />
    </button>
  );
}

// ── Session Name Field ────────────────────────────────────────────────────────
// Optional custom name so open sessions are distinguishable. Placeholder shows
// the auto-generated default that is used when left blank.

function SessionNameField({ value, onChange, placeholder, accent }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  accent: string;
}) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Name this session</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 60))}
        placeholder={placeholder}
        maxLength={60}
        className={`mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 ${accent}`}
      />
    </div>
  );
}

// ── Upload Filter Chips ───────────────────────────────────────────────────────
// Under "My Questions", lets the user narrow a session to content generated
// from specific uploads.

function UploadFilterChips({ uploads, selected, onToggle, countFor, accentClass, showAuthor }: {
  uploads: { id: string; title: string; createdAt: string; questionCount: number; flashcardCount: number; quickCount: number; authorName?: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  countFor: (u: { questionCount: number; flashcardCount: number; quickCount: number }) => number;
  accentClass: string;
  showAuthor?: boolean;
}) {
  const usable = uploads.filter(u => countFor(u) > 0);
  if (usable.length === 0) return null;
  return (
    <div className="mt-2">
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
        {showAuthor ? "Friends' uploads" : 'From specific uploads'} {selected.size > 0 ? `(${selected.size} selected)` : '(all)'}
      </div>
      <div className="max-h-28 overflow-y-auto space-y-1.5 pr-1">
        {usable.map(u => {
          const isSelected = selected.has(u.id);
          return (
            <button
              key={u.id}
              onClick={() => onToggle(u.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-all active:scale-[0.98] ${
                isSelected ? accentClass : 'border-slate-200 text-slate-600'
              }`}
            >
              <span className="text-xs font-medium truncate">
                {showAuthor && u.authorName ? <span className="text-slate-400">{u.authorName}: </span> : null}
                {u.title}
              </span>
              <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                {countFor(u)} &#xB7; {new Date(u.createdAt).toLocaleDateString()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// 3-way question source tabs: All / Mine / Friends.
function SourceTabs({ value, onChange, counts, accent, mineLabel, friendsLabel, friendsLocked, onLockedFriends }: {
  value: QSource;
  onChange: (s: QSource) => void;
  counts: { all: number; mine: number; friends: number; professor?: number };
  accent: string;
  mineLabel: string;
  friendsLabel: string;
  friendsLocked?: boolean;
  onLockedFriends?: () => void;
}) {
  const tab = (key: QSource, label: string, n: number, locked?: boolean) => (
    <button
      onClick={() => { if (locked) { onLockedFriends?.(); return; } onChange(key); }}
      className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all inline-flex items-center justify-center gap-1 ${value === key ? `bg-white ${accent} shadow-sm` : 'text-slate-500'} ${locked ? 'opacity-70' : ''}`}
    >
      {locked && (
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-amber-500">
          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
        </svg>
      )}
      {label} ({n})
    </button>
  );
  return (
    <div className="mt-3 flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
      {tab('all', 'All', counts.all)}
      {Boolean(counts.professor) && tab('professor', 'Professor', counts.professor!)}
      {tab('mine', mineLabel, counts.mine)}
      {tab('friends', friendsLabel, counts.friends, friendsLocked)}
    </div>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Comments Section ──────────────────────────────────────────────────────────

function CommentsSection({
  classSectionId,
  comments,
  entitlements,
  postComment,
  deleteComment,
}: {
  classSectionId: string;
  comments: ClassComment[];
  entitlements?: { canPost: boolean; canReply: boolean; canSeeReplies: boolean };
  postComment: ReturnType<typeof usePostComment>;
  deleteComment: ReturnType<typeof useDeleteComment>;
}) {
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const handlePost = () => {
    if (!newComment.trim()) return;
    postComment.mutate({ classSectionId, body: newComment.trim() }, {
      onSuccess: () => setNewComment(''),
    });
  };

  const handleReply = (parentId: string) => {
    if (!replyText.trim()) return;
    postComment.mutate({ classSectionId, parentId, body: replyText.trim() }, {
      onSuccess: () => { setReplyTo(null); setReplyText(''); },
    });
  };

  const handleDelete = (id: string) => {
    deleteComment.mutate({ id, classSectionId });
  };
  const canPost = entitlements?.canPost ?? false;
  const canReply = entitlements?.canReply ?? false;
  const canSeeReplies = entitlements?.canSeeReplies ?? false;

  return (
    <section className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-card">
      <div className="px-5 pt-4 pb-2 flex items-baseline justify-between">
        <h2 className="font-semibold text-squishy-dark text-sm">Discussion</h2>
        {comments.length > 0 && (
          <span className="text-[10px] font-medium text-slate-400">{comments.length} thread{comments.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {canPost ? (
        <div className="px-5 pb-3 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePost()}
            placeholder="Share a tip or ask the class..."
            className="flex-1 border border-slate-200 bg-slate-50 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary transition-colors"
          />
          <Pressable
            onClick={handlePost}
            disabled={!newComment.trim() || postComment.isPending}
            className="bg-squishy-primary text-white px-4 min-h-[44px] rounded-xl text-sm font-semibold hover:bg-squishy-tealDark transition-colors disabled:opacity-40 shadow-sm"
          >
            Post
          </Pressable>
        </div>
      ) : (
        <div className="mx-5 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800 dark:text-amber-300">
          Upgrade to Plus or Pro to join class discussions. Free users can preview the top comment.
        </div>
      )}

      {/* Comments list */}
      <div className="divide-y divide-slate-100">
        {comments.length === 0 && (
          <div className="px-5 py-8 text-center">
            <div className="mx-auto mb-2 w-10 h-10 rounded-2xl bg-teal-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-squishy-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
            </div>
            <p className="text-slate-400 text-sm">No comments yet. Start the conversation.</p>
          </div>
        )}
        <AnimatePresence initial={false}>
          {comments.map((c) => (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={springs.soft}
              className="px-5 py-3.5"
            >
              <CommentRow comment={c} onReply={canReply ? () => setReplyTo(replyTo === c.id ? null : c.id) : undefined} onDelete={() => handleDelete(c.id)} />

              {/* Replies */}
              {canSeeReplies && c.replies.length > 0 && (
                <div className="ml-9 mt-2.5 space-y-2.5 border-l-2 border-teal-100 pl-3.5">
                  {c.replies.map((r) => (
                    <CommentRow key={r.id} comment={r} isReply onDelete={() => handleDelete(r.id)} />
                  ))}
                </div>
              )}

              {/* Reply input */}
              <AnimatePresence initial={false}>
                {replyTo === c.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="ml-9 overflow-hidden"
                  >
                    <div className="mt-2.5 flex gap-2">
                      <input
                        type="text"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReply(c.id)}
                        placeholder="Reply..."
                        autoFocus
                        className="flex-1 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-squishy-primary/30 focus:border-squishy-primary transition-colors"
                      />
                      <Pressable
                        onClick={() => handleReply(c.id)}
                        disabled={!replyText.trim() || postComment.isPending}
                        className="bg-squishy-primary text-white px-3.5 min-h-[40px] rounded-xl text-xs font-semibold hover:bg-squishy-tealDark transition-colors disabled:opacity-40"
                      >
                        Reply
                      </Pressable>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}

function CommentRow({
  comment,
  isReply,
  onReply,
  onDelete,
}: {
  comment: ClassComment;
  isReply?: boolean;
  onReply?: () => void;
  onDelete: () => void;
}) {
  const timeAgo = formatTimeAgo(comment.createdAt);

  return (
    <div>
      <div className="flex items-start gap-2.5">
        <div className={`${isReply ? 'w-6 h-6 text-[9px]' : 'w-7 h-7 text-[10px]'} rounded-full bg-gradient-to-br from-teal-100 to-violet-100 text-squishy-navy overflow-hidden flex items-center justify-center font-semibold shrink-0 ring-2 ring-white shadow-sm`}>
          {comment.author.avatarUrl
            ? <img src={comment.author.avatarUrl} alt="" className="w-full h-full object-cover" />
            : comment.author.displayName[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className={`font-semibold ${isReply ? 'text-xs' : 'text-sm'} text-squishy-dark`}>{comment.author.displayName}</span>
            <span className="text-[10px] text-slate-400">{timeAgo}</span>
          </div>
          <p className={`${isReply ? 'text-xs' : 'text-sm'} text-slate-700 mt-0.5 leading-relaxed`}>{comment.body}</p>
          <div className="flex gap-1.5 mt-0.5 -ml-1.5">
            {onReply && (
              <button onClick={onReply} className="text-[11px] text-slate-400 hover:text-squishy-primary font-semibold px-1.5 py-1.5 rounded-md transition-colors">Reply</button>
            )}
            {comment.isOwn && (
              <button onClick={onDelete} className="text-[11px] text-slate-400 hover:text-red-500 font-semibold px-1.5 py-1.5 rounded-md transition-colors">Delete</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
